/* eslint-disable import/no-duplicates */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Icon,
  useDisclosure,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  HStack,
  Divider,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Link,
  Image,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import {
  RiEyeLine,
  RiFileTextLine,
  RiCheckboxCircleLine,
  RiFileCopyLine,
  RiMailSendLine,
  RiSendPlaneLine,
  RiAttachmentLine,
  RiDownloadLine,
  RiFilePdfLine,
  RiImage2Line,
  RiExternalLinkLine,
} from 'react-icons/ri';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import { ModalComponent } from '..';
import {
  IExpenseReport,
  IExpenseItem,
  getKmRate,
  isFixedRateKmObservation,
  SPECIAL_SECTOR_RATE,
} from '../../../../pages/Dashboard/vehicles_management/Expenses';
import { generateExpensePDF } from '../../../../utils/generateExpensePDF';
import { formatReportDate } from '../../../../utils/formatReportDate';
import {
  computeAllocationValue,
  IRateioItem,
} from '../../Miles/ExpenseRateio';
import { api } from '../../../../services/api';
import { useUsers } from '../../../../hooks/useUsers';
import {
  expenseReportsService,
  IExpenseEmailLog,
  IExpenseRevertLog,
} from '../../../../services/expenseReportsService';
import { apllyToast } from '../../../Global/Toast2.0';

interface IModalProps {
  expense: IExpenseReport;
  // Somente leitura (ex.: tela Controle de KM): esconde os botões que
  // disparam ações — e-mails, links de assinatura e lançamento contábil.
  readOnly?: boolean;
}

const signatureFonts: Record<string, string> = {
  'cursive-1': 'Dancing Script, cursive',
  'cursive-2': 'Pacifico, cursive',
  'cursive-3': 'Great Vibes, cursive',
  'elegant-1': 'Playfair Display, serif',
  'elegant-2': 'Cormorant Garamond, serif',
};

const emailLogTypeLabels: Record<string, string> = {
  participant_created: 'Inclusão de participante',
  participant_updated: 'Atualização de participante',
  reimbursement: 'Reembolso',
  signature: 'Assinatura do responsável',
  participant_signature: 'Assinatura do vinculado',
  manager_signature: 'Assinatura do gestor',
  accounting: 'Contabilidade',
};

// Rótulo/cor do status de assinatura de cada etapa da cadeia.
const signatureStatusMeta = (
  status?: string,
): { label: string; color: string } => {
  switch (status) {
    case 'signed':
      return { label: 'Assinado', color: 'green' };
    case 'sent':
      return { label: 'Aguardando assinatura', color: 'yellow' };
    case 'rejected':
      return { label: 'Recusado', color: 'red' };
    default:
      return { label: 'Pendente', color: 'gray' };
  }
};

const getExpenseColor = (type: string): string => {
  if (type === 'Refeição') return 'green';
  if (type === 'Quilometragem adicional') return 'blue';
  if (type === 'Pedágio adicional') return 'orange';
  return 'purple';
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};


export function ModalViewExpense({ expense, readOnly = false }: IModalProps) {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [sendingAccounting, setSendingAccounting] = useState(false);
  const [emailLogs, setEmailLogs] = useState<IExpenseEmailLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [revertLogs, setRevertLogs] = useState<IExpenseRevertLog[]>([]);
  const [sendingParticipants, setSendingParticipants] = useState(false);
  const { getUserName } = useUsers();

  const loadEmailLogs = useCallback(async () => {
    if (!expense.id) return;
    setLoadingLogs(true);
    try {
      const logs = await expenseReportsService.getEmailLogs(expense.id);
      setEmailLogs(logs);
    } catch (e) {
      // Endpoint pode ainda não existir no backend; falha silenciosa.
      setEmailLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [expense.id]);

  const loadRevertLogs = useCallback(async () => {
    if (!expense.id) return;
    try {
      const logs = await expenseReportsService.getRevertLogs(expense.id);
      setRevertLogs(logs);
    } catch (e) {
      // Endpoint pode ainda não existir no backend; falha silenciosa.
      setRevertLogs([]);
    }
  }, [expense.id]);

  useEffect(() => {
    if (isOpen) {
      loadEmailLogs();
      loadRevertLogs();
    }
  }, [isOpen, loadEmailLogs, loadRevertLogs]);

  const handleSendParticipantsEmail = async () => {
    if (!expense.id) return;
    setSendingParticipants(true);
    try {
      await expenseReportsService.sendParticipantsEmail(expense.id);
      apllyToast('success', 'E-mails enviados aos participantes.');
      await loadEmailLogs();
    } catch (e) {
      apllyToast('error', 'Não foi possível enviar os e-mails.');
    } finally {
      setSendingParticipants(false);
    }
  };

  // Extrair taxa de KM salva nas observations do item (se houver)
  const getItemKmRate = (item: IExpenseItem): number => {
    if (item.observations) {
      try {
        const parsed = JSON.parse(item.observations);
        if (parsed.km_rate) return parsed.km_rate;
      } catch (e) {
        // Não é JSON, ignorar
      }
    }
    return 0; // 0 = usar tabela padrão
  };

  // Calcular KM total (apenas do grupo "por tabela", sem taxa fixa) para
  // determinar a faixa e a taxa padrão
  const totalKmQtd = expense.items.reduce((s, i) => {
    if (
      i.expense_type === 'Quilometragem adicional' &&
      !isFixedRateKmObservation(i.observations)
    ) {
      return s + (Number(i.additional_km) || 0);
    }
    return s;
  }, 0);
  const sectorId = expense.user?.sector_id;
  const defaultKmRate = getKmRate(totalKmQtd, sectorId);

  // Taxa por item: usa a taxa salva no item ou a padrão da tabela
  const getItemEffectiveKmRate = (item: IExpenseItem): number => {
    const savedRate = getItemKmRate(item);
    return savedRate || defaultKmRate;
  };

  const getItemValueWithRate = (item: IExpenseItem): number => {
    if (item.expense_type === 'Quilometragem adicional') {
      return (Number(item.additional_km) || 0) * getItemEffectiveKmRate(item);
    }
    return Number(item.amount) || 0;
  };

  const getTotalValue = () => {
    return expense.items.reduce(
      (sum, item) => sum + getItemValueWithRate(item),
      0,
    );
  };

  const getCategoryTotals = () => {
    const totals = {
      refeicao: 0,
      km: 0,
      kmQtd: totalKmQtd,
      // Km com taxa fixa (diretoria/lista especial) — somado à parte
      kmFixo: 0,
      kmFixoQtd: 0,
      pedagio: 0,
      outros: 0,
    };

    expense.items.forEach(item => {
      if (item.expense_type === 'Refeição') {
        totals.refeicao += Number(item.amount) || 0;
      } else if (item.expense_type === 'Quilometragem adicional') {
        const km = Number(item.additional_km) || 0;
        const valor = km * getItemEffectiveKmRate(item);
        if (isFixedRateKmObservation(item.observations)) {
          totals.kmFixo += valor;
          totals.kmFixoQtd += km;
        } else {
          totals.km += valor;
        }
      } else if (item.expense_type === 'Pedágio adicional') {
        totals.pedagio += Number(item.amount) || 0;
      } else {
        totals.outros += Number(item.amount) || 0;
      }
    });

    return totals;
  };

  const getSignatureStatusText = () => {
    if (expense.signatureStatus === 'sent') {
      return 'Aguardando Assinatura Digital';
    }
    if (expense.signatureStatus === 'rejected') {
      return 'Assinatura Rejeitada';
    }
    return 'Pendente de Assinatura Digital';
  };

  const getSignatureStatusColor = () => {
    if (expense.signatureStatus === 'sent') return 'orange';
    if (expense.signatureStatus === 'rejected') return 'red';
    return 'gray';
  };

  const handleGeneratePDF = () => {
    generateExpensePDF(expense);
  };

  const getReceiptUrl = (receipt: IExpenseItem['receipt']) => {
    if (!receipt || receipt instanceof File) return null;
    if (receipt.url) {
      if (receipt.url.startsWith('http')) return receipt.url;
      return `${api.defaults.baseURL}${receipt.url}`;
    }
    if (receipt.filename) {
      return `${api.defaults.baseURL}/uploads/receipts/${receipt.filename}`;
    }
    return null;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageMime = (mime?: string) => !!mime && mime.startsWith('image/');

  const renderReceipt = (item: IExpenseItem) => {
    if (!item.receipt || item.receipt instanceof File) return null;

    const { receipt } = item;
    const url = getReceiptUrl(receipt);
    const displayName = receipt.original_name || receipt.name || 'Comprovante';
    const mime = receipt.mime_type || receipt.type;
    const isImage = isImageMime(mime);
    const size = formatFileSize(receipt.size);

    if (!url) {
      return (
        <HStack spacing={1}>
          <Icon as={RiAttachmentLine} fontSize="12" color="gray.500" />
          <Text fontSize="xs" color="gray.500">
            {displayName}
          </Text>
        </HStack>
      );
    }

    return (
      <Box
        mt={1}
        p={2}
        bg="whiteAlpha.100"
        borderRadius="md"
        border="1px solid"
        borderColor="whiteAlpha.200"
        maxW="320px"
      >
        <HStack spacing={3} align="center">
          {isImage ? (
            <Link href={url} isExternal>
              <Image
                src={url}
                alt={displayName}
                boxSize="48px"
                objectFit="cover"
                borderRadius="sm"
                fallback={(
                  <Box
                    boxSize="48px"
                    bg="whiteAlpha.200"
                    borderRadius="sm"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={RiImage2Line} fontSize="20" color="gray.400" />
                  </Box>
                )}
              />
            </Link>
          ) : (
            <Box
              boxSize="48px"
              bg="red.900"
              borderRadius="sm"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={RiFilePdfLine} fontSize="24" color="red.300" />
            </Box>
          )}

          <VStack align="start" spacing={0} flex={1} minW={0}>
            <Text
              fontSize="xs"
              color="gray.200"
              fontWeight="medium"
              noOfLines={1}
              w="100%"
            >
              {displayName}
            </Text>
            {size && (
              <Text fontSize="2xs" color="gray.500">
                {size}
              </Text>
            )}
          </VStack>

          <HStack spacing={1}>
            <Tooltip label="Visualizar" hasArrow placement="top">
              <IconButton
                as={Link}
                href={url}
                isExternal
                aria-label="Visualizar comprovante"
                icon={<Icon as={RiExternalLinkLine} />}
                size="xs"
                variant="ghost"
                colorScheme="blue"
              />
            </Tooltip>
            <Tooltip label="Baixar" hasArrow placement="top">
              <IconButton
                as="a"
                href={url}
                download={displayName}
                aria-label="Baixar comprovante"
                icon={<Icon as={RiDownloadLine} />}
                size="xs"
                variant="ghost"
                colorScheme="green"
              />
            </Tooltip>
          </HStack>
        </HStack>
      </Box>
    );
  };

  const totalValue = getTotalValue();
  const categoryTotals = getCategoryTotals();
  const isConsolidated = expense.isConsolidated || expense.is_consolidated;

  // Itens simplificados para resolver o rateio no modo "por itens"
  const rateioItems: IRateioItem[] = expense.items.map(item => ({
    id: item.id,
    label: item.expense_type,
    value: getItemValueWithRate(item),
  }));

  const {rateio} = expense;
  const rateioModeLabels: Record<string, string> = {
    percentual: 'Percentual',
    valores: 'Valores',
    itens: 'Por itens',
  };

  // Helpers para status do gestor (evitar ternários aninhados no JSX)
  // Membros da diretoria não têm gestor — nesse caso não há aprovação
  // pendente, então a etapa é considerada concluída assim que o
  // responsável assina.
  const hasManager = !!expense.manager_id;
  const isManagerSigned =
    !hasManager ||
    !!expense.managerSignature ||
    expense.managerSignatureStatus === 'signed' ||
    expense.manager_signature_status === 'signed';
  const isManagerSent =
    expense.managerSignatureStatus === 'sent' ||
    expense.manager_signature_status === 'sent';

  const getManagerBadgeColor = () => {
    if (isManagerSigned) return 'green';
    if (isManagerSent) return 'orange';
    return 'gray';
  };

  const getManagerBadgeLabel = () => {
    if (isManagerSigned) return 'Aprovado';
    if (isManagerSent) return 'Aguardando';
    return 'Pendente';
  };

  const getStatusColor = () => {
    if (expense.status === 'LANCADO') return 'green';
    if (expense.status === 'RECUSADO') return 'red';
    return 'yellow';
  };

  // Cadeia de assinatura na ordem real: responsável (0) -> vinculados
  // (ordem de cadastro) -> gestor. Usado pelo rastreador para mostrar
  // "qual etapa está".
  const orderedParticipants = [...(expense.participants || [])].sort(
    (a, b) => (a.signing_order ?? 99) - (b.signing_order ?? 99),
  );

  const responsibleStatus =
    expense.signature_status || expense.signatureStatus;
  const responsibleSignedAt = expense.signed_at || expense.signedAt;
  const managerStatus =
    expense.manager_signature_status || expense.managerSignatureStatus;
  const managerSignedAt = expense.manager_signed_at || expense.managerSignedAt;

  type ChainStep = {
    key: string;
    order: number | string;
    label: string;
    name: string;
    email?: string;
    status?: string;
    signedAt?: string;
  };

  const signatureChain: ChainStep[] = [];

  orderedParticipants.forEach(p => {
    const isResponsible = p.role === 'responsible';
    signatureChain.push({
      key: p.id || p.user_id || `${p.role}-${p.signing_order}`,
      order: p.signing_order ?? (isResponsible ? 0 : '-'),
      label: isResponsible ? 'Responsável' : 'Vinculado',
      name: p.name || getUserName(p.user_id) || p.user?.name || '-',
      // E-mail do envolvido (responsável usa o e-mail do relatório como fallback).
      email: p.user?.email || (isResponsible ? expense.user?.email : undefined),
      // O status do responsável vem do relatório; o do vinculado, do participante.
      status: isResponsible ? responsibleStatus : p.signature_status,
      signedAt: isResponsible ? responsibleSignedAt : p.signed_at,
    });
  });

  // Etapa final: gestor. Não existe para membros da diretoria, que não
  // possuem gestor vinculado ao relatório.
  if (hasManager) {
    signatureChain.push({
      key: 'manager',
      order: 'Gestor',
      label: 'Gestor',
      name: expense.manager?.name || '-',
      email: expense.manager?.email,
      status: managerStatus,
      signedAt: managerSignedAt,
    });
  }

  // Identifica a etapa atual (primeira não assinada/recusada) para destaque.
  const currentStepIndex = signatureChain.findIndex(
    step => step.status !== 'signed' && step.status !== 'rejected',
  );

  return (
    <>
      <Button
        size="sm"
        colorScheme="blue"
        variant="ghost"
        onClick={onOpen}
        title="Visualizar relatório"
      >
        <Icon as={RiEyeLine} fontSize="20" />
      </Button>

      <ModalComponent
        title="Relatório de Despesas e Reembolsos"
        isOpen={isOpen}
        onClose={onClose}
        sizeSpecifyc="3xl"
        scrollBehavior="inside"
      >
        <ModalBody maxH="75vh" overflowY="auto">
          <VStack spacing={4} align="stretch">
            {/* Indicador de Consolidado */}
            {isConsolidated && (
              <HStack
                spacing={2}
                bg="whiteAlpha.100"
                p={3}
                borderRadius="md"
                borderLeft="3px solid"
                borderLeftColor="green.400"
              >
                <Icon
                  as={RiCheckboxCircleLine}
                  color="green.400"
                  fontSize="18"
                />
                <Text fontSize="sm" color="green.300">
                  Relatório consolidado
                  {expense.consolidatedAt || expense.consolidated_at
                    ? ` em ${format(
                        new Date(
                          (expense.consolidatedAt ||
                            expense.consolidated_at) as string,
                        ),
                        "dd/MM/yyyy 'às' HH:mm",
                        { locale: ptBR },
                      )}`
                    : ''}
                </Text>
              </HStack>
            )}

            {/* Informações do Relatório */}
            <Box bg="whiteAlpha.100" p={4} borderRadius="md">
              <HStack justify="space-between" mb={4}>
                <Text fontSize="md" fontWeight="bold" color="gray.300">
                  Informações do Relatório
                </Text>
                <Badge colorScheme="blue" px={2}>
                  {expense.items.length}{' '}
                  {expense.items.length === 1 ? 'ITEM' : 'ITENS'}
                </Badge>
              </HStack>

              <HStack spacing={8} mb={3}>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Usuário Responsável
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color="gray.300">
                    {expense.director_name || getUserName(expense.director)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Data
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    {formatReportDate(expense.date, "dd 'de' MMMM 'de' yyyy")}
                  </Text>
                </Box>
              </HStack>

              <HStack spacing={8} mb={3}>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Setor
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    {expense.sector}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Seção
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    {expense.section}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Centro de Custo
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    {expense.cost_center}
                  </Text>
                </Box>
                {expense.branch && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Filial
                    </Text>
                    <Text fontSize="sm" color="gray.300">
                      {expense.branch.short_name}
                    </Text>
                  </Box>
                )}
              </HStack>

              {/* Status do Lançamento e Vencimento */}
              {expense.status && (
                <HStack spacing={8}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Status
                    </Text>
                    <Badge
                      colorScheme={getStatusColor()}
                      fontSize="xs"
                    >
                      {expense.status}
                    </Badge>
                  </Box>
                  {expense.due_date && (
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Vencimento
                      </Text>
                      <Text fontSize="sm" color="gray.300">
                        {formatReportDate(expense.due_date, 'dd/MM/yyyy')}
                      </Text>
                    </Box>
                  )}
                  {expense.status === 'RECUSADO' &&
                    expense.observation && (
                      <Box flex={1}>
                        <Text fontSize="xs" color="gray.500">
                          Motivo da Recusa
                        </Text>
                        <Text fontSize="sm" color="red.300">
                          {expense.observation}
                        </Text>
                      </Box>
                    )}
                </HStack>
              )}
            </Box>

            {/* Dados Bancários (do relatório ou do usuário) */}
            {(expense.pix_key ||
              expense.bank_name ||
              expense.user?.pix_key ||
              expense.user?.bank_name) && (
              <Box
                bg="whiteAlpha.100"
                p={4}
                borderRadius="md"
                borderLeft="3px solid"
                borderLeftColor="blue.400"
              >
                <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={2}>
                  DADOS BANCÁRIOS PARA RESSARCIMENTO
                </Text>
                <HStack spacing={6} flexWrap="wrap">
                  {(expense.pix_key || expense.user?.pix_key) && (
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Chave PIX
                      </Text>
                      <Text fontSize="sm" color="gray.300">
                        {expense.pix_key || expense.user?.pix_key}
                      </Text>
                    </Box>
                  )}
                  {(expense.bank_name || expense.user?.bank_name) && (
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Banco
                      </Text>
                      <Text fontSize="sm" color="gray.300">
                        {expense.bank_name || expense.user?.bank_name}
                      </Text>
                    </Box>
                  )}
                  {(expense.bank_agency || expense.user?.bank_agency) && (
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Agência
                      </Text>
                      <Text fontSize="sm" color="gray.300">
                        {expense.bank_agency || expense.user?.bank_agency}
                      </Text>
                    </Box>
                  )}
                  {(expense.bank_account || expense.user?.bank_account) && (
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Conta
                      </Text>
                      <Text fontSize="sm" color="gray.300">
                        {expense.bank_account || expense.user?.bank_account}
                      </Text>
                    </Box>
                  )}
                </HStack>
              </Box>
            )}

            {/* Participantes (Responsável e Vinculados) */}
            {expense.participants && expense.participants.length > 0 && (
              <Box
                bg="whiteAlpha.100"
                p={4}
                borderRadius="md"
                borderLeft="3px solid"
                borderLeftColor="blue.400"
              >
                <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={3}>
                  PARTICIPANTES
                </Text>
                <VStack align="stretch" spacing={3}>
                  {expense.participants.map(p => (
                    <Box
                      key={p.id || p.user_id}
                      p={3}
                      bg="whiteAlpha.50"
                      borderRadius="md"
                    >
                      <HStack justify="space-between" mb={1} flexWrap="wrap">
                        <HStack spacing={2}>
                          <Badge
                            colorScheme={
                              p.role === 'responsible' ? 'blue' : 'gray'
                            }
                          >
                            {p.role === 'responsible'
                              ? 'Responsável'
                              : 'Vinculado'}
                          </Badge>
                          <Text fontSize="sm" color="gray.200" fontWeight="600">
                            {p.name || getUserName(p.user_id)}
                          </Text>
                        </HStack>
                        <Badge
                          colorScheme={p.payment_required ? 'green' : 'orange'}
                        >
                          {p.payment_required
                            ? 'Pagamento necessário'
                            : 'Sem pagamento (responsável arcou)'}
                        </Badge>
                      </HStack>
                      <HStack spacing={6} flexWrap="wrap" mt={2}>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Setor
                          </Text>
                          <Text fontSize="sm" color="gray.300">
                            {p.sector || '-'}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Seção
                          </Text>
                          <Text fontSize="sm" color="gray.300">
                            {p.section || '-'}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Centro de Custo
                          </Text>
                          <Text fontSize="sm" color="gray.300">
                            {p.cost_center || '-'}
                          </Text>
                        </Box>
                        {p.payment_required && p.pix_key && (
                          <Box>
                            <Text fontSize="xs" color="gray.500">
                              Chave PIX
                            </Text>
                            <Text fontSize="sm" color="gray.300">
                              {p.pix_key}
                            </Text>
                          </Box>
                        )}
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Rastreador da cadeia de assinatura */}
            {signatureChain.length > 0 && (
              <Box
                bg="whiteAlpha.100"
                p={4}
                borderRadius="md"
                borderLeft="3px solid"
                borderLeftColor="cyan.400"
              >
                <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={3}>
                  ETAPAS DA ASSINATURA
                </Text>
                <VStack align="stretch" spacing={2}>
                  {signatureChain.map((step, index) => {
                    const meta = signatureStatusMeta(step.status);
                    const isCurrent = index === currentStepIndex;
                    // A etapa atual é destacada de forma discreta: fundo neutro
                    // (igual às demais linhas) e apenas uma borda lateral azul
                    // mais grossa, mantendo a harmonia com o tema escuro.
                    return (
                      <HStack
                        key={step.key}
                        p={2}
                        borderRadius="md"
                        bg="whiteAlpha.50"
                        borderLeft={isCurrent ? '4px solid' : '3px solid'}
                        borderLeftColor={
                          isCurrent ? 'blue.300' : 'transparent'
                        }
                        justify="space-between"
                        flexWrap="wrap"
                      >
                        <HStack spacing={3}>
                          <Badge colorScheme="blackAlpha" fontSize="0.65rem">
                            {typeof step.order === 'number'
                              ? `#${step.order + 1}`
                              : step.order}
                          </Badge>
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.200"
                              fontWeight="600"
                            >
                              {step.name}
                            </Text>
                            {step.email && (
                              <Text fontSize="xs" color="gray.400">
                                {step.email}
                              </Text>
                            )}
                            <Text
                              fontSize="xs"
                              color={isCurrent ? 'blue.300' : 'gray.500'}
                              fontWeight={isCurrent ? '600' : 'normal'}
                            >
                              {step.label}
                              {isCurrent ? ' • etapa atual' : ''}
                            </Text>
                          </Box>
                        </HStack>
                        <HStack spacing={3}>
                          {step.signedAt && step.status === 'signed' && (
                            <Text fontSize="xs" color="gray.400">
                              {format(
                                new Date(step.signedAt),
                                'dd/MM/yyyy HH:mm',
                                { locale: ptBR },
                              )}
                            </Text>
                          )}
                          <Badge colorScheme={meta.color}>{meta.label}</Badge>
                        </HStack>
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>
            )}

            {/* Rateio por Centro de Custo */}
            {rateio && rateio.allocations.length > 0 && (
              <Box
                bg="whiteAlpha.100"
                p={4}
                borderRadius="md"
                borderLeft="3px solid"
                borderLeftColor="purple.400"
              >
                <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={3}>
                  RATEIO POR CENTRO DE CUSTO (
                  {rateioModeLabels[rateio.mode] || rateio.mode})
                </Text>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th color="gray.500">Centro de Custo</Th>
                      <Th color="gray.500">Setor/Seção</Th>
                      <Th color="gray.500" isNumeric>
                        Valor
                      </Th>
                      <Th color="gray.500" isNumeric>
                        % do total
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rateio.allocations.map(alloc => {
                      const value = computeAllocationValue(
                        alloc,
                        rateio.mode,
                        totalValue,
                        rateioItems,
                      );
                      const pct =
                        totalValue > 0
                          ? ((value / totalValue) * 100).toFixed(1)
                          : '0.0';
                      return (
                        <Tr key={alloc.id || alloc.cost_center}>
                          <Td color="gray.300">{alloc.cost_center || '-'}</Td>
                          <Td color="gray.400" fontSize="xs">
                            {[alloc.sector, alloc.section]
                              .filter(Boolean)
                              .join(' / ') || '-'}
                          </Td>
                          <Td color="gray.300" isNumeric>
                            {formatCurrency(value)}
                          </Td>
                          <Td color="gray.400" isNumeric>
                            {pct}%
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}

            {/* E-mails enviados aos envolvidos */}
            <Box
              bg="whiteAlpha.100"
              p={4}
              borderRadius="md"
              borderLeft="3px solid"
              borderLeftColor="teal.400"
            >
              <HStack justify="space-between" mb={3}>
                <Text fontSize="xs" color="gray.500" fontWeight="bold">
                  E-MAILS ENVIADOS
                </Text>
                {!readOnly && (
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="teal"
                    isLoading={sendingParticipants}
                    leftIcon={<Icon as={RiMailSendLine} fontSize="12" />}
                    onClick={handleSendParticipantsEmail}
                  >
                    Reenviar aos participantes
                  </Button>
                )}
              </HStack>

              {loadingLogs && (
                <Text fontSize="xs" color="gray.500">
                  Carregando histórico...
                </Text>
              )}

              {!loadingLogs && emailLogs.length === 0 && (
                <Text fontSize="xs" color="gray.500">
                  Nenhum e-mail enviado para este relatório.
                </Text>
              )}

              {!loadingLogs && emailLogs.length > 0 && (
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th color="gray.500">Destinatário</Th>
                      <Th color="gray.500">Etapa</Th>
                      <Th color="gray.500">Status</Th>
                      <Th color="gray.500">Enviado em</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {emailLogs.map(log => (
                      <Tr key={log.id}>
                        <Td color="gray.300" fontSize="xs">
                          {log.recipient_email}
                        </Td>
                        <Td color="gray.400" fontSize="xs">
                          {emailLogTypeLabels[log.type] || log.type}
                          {typeof log.signing_order === 'number' && (
                            <Text as="span" color="gray.600">
                              {' '}
                              (#{log.signing_order + 1})
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={log.status === 'sent' ? 'green' : 'red'}
                          >
                            {log.status === 'sent' ? 'Enviado' : 'Falhou'}
                          </Badge>
                          {log.status === 'failed' && log.error && (
                            <Text fontSize="0.6rem" color="red.300" mt={1}>
                              {log.error}
                            </Text>
                          )}
                        </Td>
                        <Td color="gray.400" fontSize="xs">
                          {log.sent_at
                            ? format(new Date(log.sent_at), 'dd/MM/yyyy HH:mm', {
                                locale: ptBR,
                              })
                            : '-'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>

            {/* Histórico de reaberturas (reversões) - auditoria */}
            {revertLogs.length > 0 && (
              <Box
                bg="whiteAlpha.100"
                p={4}
                borderRadius="md"
                borderLeft="3px solid"
                borderLeftColor="orange.400"
              >
                <Text
                  fontSize="xs"
                  color="gray.500"
                  fontWeight="bold"
                  mb={3}
                >
                  HISTÓRICO DE REABERTURAS
                </Text>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th color="gray.500">Reaberto por</Th>
                      <Th color="gray.500">Data</Th>
                      <Th color="gray.500">Motivo</Th>
                      <Th color="gray.500">Estado anterior</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {revertLogs.map(log => (
                      <Tr key={log.id}>
                        <Td color="gray.300" fontSize="xs">
                          {log.reverted_by_name || '—'}
                        </Td>
                        <Td color="gray.400" fontSize="xs">
                          {log.reverted_at
                            ? format(
                                new Date(log.reverted_at),
                                'dd/MM/yyyy HH:mm',
                                { locale: ptBR },
                              )
                            : '-'}
                        </Td>
                        <Td color="gray.300" fontSize="xs">
                          {log.reason || '—'}
                        </Td>
                        <Td color="gray.400" fontSize="xs">
                          {log.previous_status || 'EM PROCESSO'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}

            {/* Resumo por Categoria */}
            <HStack spacing={3} flexWrap="wrap">
              {categoryTotals.refeicao > 0 && (
                <Box
                  bg="whiteAlpha.100"
                  borderLeft="3px solid"
                  borderLeftColor="green.400"
                  px={4}
                  py={3}
                  borderRadius="md"
                  flex={1}
                  minW="140px"
                >
                  <Text fontSize="xs" color="gray.500">
                    Refeições
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color="green.300">
                    {formatCurrency(categoryTotals.refeicao)}
                  </Text>
                </Box>
              )}
              {categoryTotals.km > 0 && (
                <Box
                  bg="whiteAlpha.100"
                  borderLeft="3px solid"
                  borderLeftColor="blue.400"
                  px={4}
                  py={3}
                  borderRadius="md"
                  flex={1}
                  minW="140px"
                >
                  <Text fontSize="xs" color="gray.500">
                    Quilometragem ({categoryTotals.kmQtd} km)
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color="blue.300">
                    {formatCurrency(categoryTotals.km)}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    R${' '}
                    {(categoryTotals.kmQtd > 0
                      ? categoryTotals.km / categoryTotals.kmQtd
                      : defaultKmRate
                    ).toFixed(2)}
                    /km
                  </Text>
                </Box>
              )}
              {categoryTotals.kmFixo > 0 && (
                <Box
                  bg="whiteAlpha.100"
                  borderLeft="3px solid"
                  borderLeftColor="blue.400"
                  px={4}
                  py={3}
                  borderRadius="md"
                  flex={1}
                  minW="140px"
                >
                  <Text fontSize="xs" color="gray.500">
                    Quilometragem Tabela Fixa ({categoryTotals.kmFixoQtd} km)
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color="blue.300">
                    {formatCurrency(categoryTotals.kmFixo)}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    R${' '}
                    {(categoryTotals.kmFixoQtd > 0
                      ? categoryTotals.kmFixo / categoryTotals.kmFixoQtd
                      : SPECIAL_SECTOR_RATE
                    ).toFixed(2)}
                    /km
                  </Text>
                </Box>
              )}
              {categoryTotals.pedagio > 0 && (
                <Box
                  bg="whiteAlpha.100"
                  borderLeft="3px solid"
                  borderLeftColor="orange.400"
                  px={4}
                  py={3}
                  borderRadius="md"
                  flex={1}
                  minW="140px"
                >
                  <Text fontSize="xs" color="gray.500">
                    Pedágios
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color="orange.300">
                    {formatCurrency(categoryTotals.pedagio)}
                  </Text>
                </Box>
              )}
              {categoryTotals.outros > 0 && (
                <Box
                  bg="whiteAlpha.100"
                  borderLeft="3px solid"
                  borderLeftColor="purple.400"
                  px={4}
                  py={3}
                  borderRadius="md"
                  flex={1}
                  minW="140px"
                >
                  <Text fontSize="xs" color="gray.500">
                    Outros
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color="purple.300">
                    {formatCurrency(categoryTotals.outros)}
                  </Text>
                </Box>
              )}
            </HStack>

            <Divider borderColor="whiteAlpha.200" />

            {/* Despesas Detalhadas */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={3} color="gray.300">
                Despesas Detalhadas
              </Text>

              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th color="gray.500" borderColor="whiteAlpha.200" w="40px">
                      #
                    </Th>
                    <Th color="gray.500" borderColor="whiteAlpha.200">
                      Tipo
                    </Th>
                    <Th color="gray.500" borderColor="whiteAlpha.200">
                      Detalhes
                    </Th>
                    <Th
                      color="gray.500"
                      borderColor="whiteAlpha.200"
                      isNumeric
                    >
                      Valor
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {expense.items.map((item, index) => (
                    <Tr key={item.id}>
                      <Td borderColor="whiteAlpha.100" color="gray.500">
                        {index + 1}
                      </Td>
                      <Td borderColor="whiteAlpha.100">
                        <Badge
                          colorScheme={getExpenseColor(item.expense_type)}
                          fontSize="xs"
                        >
                          {item.expense_type}
                        </Badge>
                      </Td>
                      <Td
                        borderColor="whiteAlpha.100"
                        maxW="350px"
                        overflow="hidden"
                      >
                        {(() => {
                          if (item.expense_type === 'Ajuda de custo') {
                            return (
                              <VStack align="start" spacing={1} bg="whiteAlpha.50" p={2} borderRadius="md" w="full">
                                {(()=>{
                                  try {
                                    const parsed = JSON.parse(item.observations || '{}');
                                    return (
                                      <>
                                        <Text fontSize="xs" fontWeight="bold" color="teal.300">DADOS DO EVENTO</Text>
                                        <Text fontSize="xs" color="gray.300"><strong>Nome:</strong> {parsed.nome}</Text>
                                        <Text fontSize="xs" color="gray.300"><strong>Objetivo:</strong> {parsed.objetivo}</Text>
                                        <Text fontSize="xs" color="gray.300"><strong>Local:</strong> {parsed.local_evento}</Text>
                                        {parsed.endereco && <Text fontSize="xs" color="gray.300"><strong>Endereço:</strong> {parsed.endereco}</Text>}
                                        {parsed.periodo_evento && <Text fontSize="xs" color="gray.300"><strong>Período:</strong> {parsed.periodo_evento}</Text>}
                                        {parsed.horarios && parsed.horarios.map((h: any, i: number) => (
                                          // eslint-disable-next-line react/no-array-index-key
                                          <Text key={`schedule-${i}`} fontSize="xs" color="gray.300">
                                            <strong>Horário ({i + 1}):</strong> Início: {h.inicio || '-'} | Término: {h.termino || '-'}
                                          </Text>
                                        ))}
                                        <Divider my={1} borderColor="whiteAlpha.300" />
                                        <Text fontSize="xs" fontWeight="bold" color="teal.300">DADOS DA VIAGEM</Text>
                                        {parsed.dados_viagem_data && <Text fontSize="xs" color="gray.300"><strong>Data:</strong> {parsed.dados_viagem_data}</Text>}
                                        {parsed.trecho_ida && <Text fontSize="xs" color="gray.300"><strong>Ida:</strong> {parsed.trecho_ida} {parsed.trecho_ida_data ? `(em ${parsed.trecho_ida_data})` : ''}</Text>}
                                        {parsed.trecho_volta && <Text fontSize="xs" color="gray.300"><strong>Volta:</strong> {parsed.trecho_volta} {parsed.trecho_volta_data ? `(em ${parsed.trecho_volta_data})` : ''}</Text>}
                                      </>
                                    )
                                  } catch(e) {
                                    return <Text fontSize="xs" color="gray.500">{item.observations}</Text>;
                                  }
                                })()}
                                {renderReceipt(item)}
                              </VStack>
                            );
                          }

                          if (item.expense_type === 'Quilometragem adicional') {
                            let obsTexto = '';
                            try {
                              if (item.observations) {
                                const parsed = JSON.parse(item.observations);
                                if (parsed.obs) obsTexto = parsed.obs;
                              }
                            } catch (e) {
                              obsTexto = item.observations || '';
                            }

                            // Buscar fotos do hodômetro do array receipts
                            const kmStartReceipt = item.receipts?.find(
                              r => r.receipt_type === 'km_start',
                            );
                            const kmEndReceipt = item.receipts?.find(
                              r => r.receipt_type === 'km_end',
                            );

                            return (
                              <VStack align="start" spacing={2}>
                                {/* KM Percorrido */}
                                <HStack spacing={3}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="bold"
                                    color="gray.300"
                                  >
                                    {item.additional_km} km
                                  </Text>
                                  {isFixedRateKmObservation(
                                    item.observations,
                                  ) && (
                                    <Text fontSize="xs" color="blue.400">
                                      × R${' '}
                                      {getItemEffectiveKmRate(item).toFixed(
                                        2,
                                      )}
                                    </Text>
                                  )}
                                </HStack>

                                {/* Hodômetro: KM Inicial → KM Final */}
                                {(item.km_start != null ||
                                  item.km_end != null) && (
                                  <HStack
                                    spacing={4}
                                    bg="whiteAlpha.100"
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                    fontSize="xs"
                                  >
                                    <Box>
                                      <Text color="gray.500">Saída</Text>
                                      <Text
                                        color="gray.300"
                                        fontWeight="medium"
                                      >
                                        {item.km_start?.toLocaleString(
                                          'pt-BR',
                                        )}{' '}
                                        km
                                      </Text>
                                    </Box>
                                    <Text color="gray.500" fontSize="md">
                                      →
                                    </Text>
                                    <Box>
                                      <Text color="gray.500">Chegada</Text>
                                      <Text
                                        color="gray.300"
                                        fontWeight="medium"
                                      >
                                        {item.km_end?.toLocaleString('pt-BR')}{' '}
                                        km
                                      </Text>
                                    </Box>
                                  </HStack>
                                )}

                                {/* Fotos do hodômetro */}
                                {(kmStartReceipt || kmEndReceipt) && (
                                  <HStack spacing={2}>
                                    {kmStartReceipt && (
                                      <Link
                                        href={kmStartReceipt.url}
                                        isExternal
                                      >
                                        <Image
                                          src={kmStartReceipt.url}
                                          alt="Hodômetro saída"
                                          boxSize="48px"
                                          objectFit="cover"
                                          borderRadius="sm"
                                          border="1px solid"
                                          borderColor="green.600"
                                          title="Hodômetro - Saída"
                                        />
                                      </Link>
                                    )}
                                    {kmEndReceipt && (
                                      <Link
                                        href={kmEndReceipt.url}
                                        isExternal
                                      >
                                        <Image
                                          src={kmEndReceipt.url}
                                          alt="Hodômetro chegada"
                                          boxSize="48px"
                                          objectFit="cover"
                                          borderRadius="sm"
                                          border="1px solid"
                                          borderColor="red.600"
                                          title="Hodômetro - Chegada"
                                        />
                                      </Link>
                                    )}
                                  </HStack>
                                )}

                                {item.displacement_reason && (
                                  <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    wordBreak="break-word"
                                    whiteSpace="pre-wrap"
                                    maxW="100%"
                                  >
                                    Motivo: {item.displacement_reason}
                                  </Text>
                                )}
                                {obsTexto && (
                                  <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    wordBreak="break-word"
                                    whiteSpace="pre-wrap"
                                    maxW="100%"
                                  >
                                    Obs: {obsTexto}
                                  </Text>
                                )}
                              </VStack>
                            );
                          }

                          return (
                            <VStack align="start" spacing={1}>
                              {item.observations && (
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  wordBreak="break-word"
                                  whiteSpace="pre-wrap"
                                  maxW="100%"
                                >
                                  {item.observations}
                                </Text>
                              )}
                              {renderReceipt(item)}
                              {!item.receipt && !item.observations && (
                                <Text fontSize="xs" color="gray.500">
                                  —
                                </Text>
                              )}
                            </VStack>
                          );
                        })()}
                      </Td>
                      <Td
                        isNumeric
                        fontWeight="medium"
                        borderColor="whiteAlpha.100"
                        color="gray.300"
                      >
                        {/* Km "por tabela" não tem valor individual real —
                            o valor de cada item depende da soma do grupo, e
                            só o total (acima) é significativo. Km com taxa
                            fixa (1,63) tem valor próprio, então mostra. */}
                        {item.expense_type !== 'Quilometragem adicional' ||
                        isFixedRateKmObservation(item.observations)
                          ? formatCurrency(getItemValueWithRate(item))
                          : '—'}
                      </Td>
                    </Tr>
                  ))}

                  {/* Total */}
                  <Tr>
                    <Td
                      colSpan={3}
                      borderColor="whiteAlpha.300"
                      borderTopWidth="2px"
                    >
                      <Text fontWeight="bold" color="gray.300">
                        Total
                      </Text>
                    </Td>
                    <Td
                      isNumeric
                      borderColor="whiteAlpha.300"
                      borderTopWidth="2px"
                    >
                      <Text fontWeight="bold" fontSize="lg" color="blue.300">
                        {formatCurrency(totalValue)}
                      </Text>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            <Divider borderColor="whiteAlpha.200" />

            {/* Assinaturas e Links */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={3} color="gray.300">
                Assinaturas
              </Text>

              <VStack spacing={3} align="stretch">
                {/* Assinatura do Usuário */}
                <Box
                  bg="whiteAlpha.100"
                  p={4}
                  borderRadius="md"
                  borderLeft="3px solid"
                  borderLeftColor={
                    expense.signature ||
                    expense.signatureStatus === 'signed'
                      ? 'green.400'
                      : `${getSignatureStatusColor()}.400`
                  }
                >
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="xs" color="gray.500" fontWeight="bold">
                      ASSINATURA DO USUÁRIO
                    </Text>
                    <Badge
                      colorScheme={
                        expense.signature ||
                        expense.signatureStatus === 'signed'
                          ? 'green'
                          : getSignatureStatusColor()
                      }
                      fontSize="2xs"
                    >
                      {expense.signature ||
                      expense.signatureStatus === 'signed'
                        ? 'Assinado'
                        : getSignatureStatusText()}
                    </Badge>
                  </HStack>

                  {expense.signature ? (
                    <VStack align="start" spacing={0}>
                      <Text
                        fontFamily={signatureFonts[expense.signature.style]}
                        fontSize="2xl"
                        color="blue.300"
                      >
                        {expense.signature.name}
                      </Text>
                      {expense.signature.cpf && (
                        <Text fontSize="xs" color="gray.500">
                          CPF: {expense.signature.cpf}
                        </Text>
                      )}
                      {(expense.signedAt || expense.signed_at) && (
                        <Text fontSize="xs" color="gray.500">
                          Assinado em{' '}
                          {format(
                            new Date(
                              (expense.signedAt ||
                                expense.signed_at) as string,
                            ),
                            "dd/MM/yyyy 'às' HH:mm",
                            { locale: ptBR },
                          )}
                        </Text>
                      )}
                    </VStack>
                  ) : (
                    !readOnly && (
                    <HStack mt={2} spacing={2}>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        leftIcon={<Icon as={RiFileCopyLine} fontSize="12" />}
                        onClick={() => {
                          const url = `${window.location.origin}/sign-expense/${expense.id}`;
                          navigator.clipboard.writeText(url);
                          apllyToast(
                            'success',
                            'Link do usuário copiado!',
                          );
                        }}
                      >
                        Copiar Link
                      </Button>
                    </HStack>
                    )
                  )}
                </Box>

                {/* Assinaturas dos Vinculados */}
                {orderedParticipants
                  .filter(p => p.role === 'related')
                  .map(p => {
                    const meta = signatureStatusMeta(p.signature_status);
                    const isSigned = p.signature_status === 'signed';
                    return (
                      <Box
                        key={p.id || p.user_id}
                        bg="whiteAlpha.100"
                        p={4}
                        borderRadius="md"
                        borderLeft="3px solid"
                        borderLeftColor={`${meta.color}.400`}
                      >
                        <HStack justify="space-between" mb={1}>
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            fontWeight="bold"
                          >
                            ASSINATURA DO VINCULADO
                            {(p.name || p.user?.name) &&
                              ` — ${p.name || p.user?.name}`}
                          </Text>
                          <Badge colorScheme={meta.color} fontSize="2xs">
                            {meta.label}
                          </Badge>
                        </HStack>

                        {isSigned ? (
                          <VStack align="start" spacing={0}>
                            <Text
                              fontFamily={
                                signatureFonts[
                                  p.signature_style || 'cursive-1'
                                ]
                              }
                              fontSize="2xl"
                              color="blue.300"
                            >
                              {p.signature_name ||
                                p.name ||
                                p.user?.name}
                            </Text>
                            {p.signature_cpf && (
                              <Text fontSize="xs" color="gray.500">
                                CPF: {p.signature_cpf}
                              </Text>
                            )}
                            {p.signed_at && (
                              <Text fontSize="xs" color="gray.500">
                                Assinado em{' '}
                                {format(
                                  new Date(p.signed_at),
                                  "dd/MM/yyyy 'às' HH:mm",
                                  { locale: ptBR },
                                )}
                              </Text>
                            )}
                          </VStack>
                        ) : (
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {p.signature_status === 'rejected'
                              ? 'Assinatura recusada'
                              : 'Aguardando assinatura do vinculado'}
                          </Text>
                        )}
                      </Box>
                    );
                  })}

                {/* Assinatura do Gestor */}
                {expense.manager_id && (
                  <Box
                    bg="whiteAlpha.100"
                    p={4}
                    borderRadius="md"
                    borderLeft="3px solid"
                    borderLeftColor={`${getManagerBadgeColor()}.400`}
                  >
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold">
                        APROVAÇÃO DO GESTOR
                        {expense.manager?.name &&
                          ` — ${expense.manager.name}`}
                      </Text>
                      <Badge
                        colorScheme={getManagerBadgeColor()}
                        fontSize="2xs"
                      >
                        {getManagerBadgeLabel()}
                      </Badge>
                    </HStack>

                    {expense.managerSignature ? (
                      <VStack align="start" spacing={0}>
                        <Text
                          fontFamily={
                            signatureFonts[expense.managerSignature.style]
                          }
                          fontSize="2xl"
                          color="blue.300"
                        >
                          {expense.managerSignature.name}
                        </Text>
                        {expense.managerSignature.cpf && (
                          <Text fontSize="xs" color="gray.500">
                            CPF: {expense.managerSignature.cpf}
                          </Text>
                        )}
                        {(expense.managerSignedAt ||
                          expense.manager_signed_at) && (
                          <Text fontSize="xs" color="gray.500">
                            Aprovado em{' '}
                            {format(
                              new Date(
                                (expense.managerSignedAt ||
                                  expense.manager_signed_at) as string,
                              ),
                              "dd/MM/yyyy 'às' HH:mm",
                              { locale: ptBR },
                            )}
                          </Text>
                        )}
                      </VStack>
                    ) : (
                      !readOnly && (
                      <HStack mt={2} spacing={2}>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          leftIcon={
                            <Icon as={RiFileCopyLine} fontSize="12" />
                          }
                          onClick={() => {
                            const url = `${window.location.origin}/sign-expense/${expense.id}?role=manager`;
                            navigator.clipboard.writeText(url);
                            apllyToast('success', 'Link do gestor copiado!');
                          }}
                        >
                          Copiar Link
                        </Button>
                        {expense.manager?.email && (
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="teal"
                            leftIcon={
                              <Icon as={RiMailSendLine} fontSize="12" />
                            }
                            onClick={async () => {
                              try {
                                const url = `${window.location.origin}/sign-expense/${expense.id}?role=manager`;
                                await expenseReportsService.sendManagerSignatureEmail(
                                  expense.id,
                                  {
                                    url,
                                    email: expense.manager?.email,
                                    name: expense.manager?.name,
                                    subject:
                                      'Aprovação de Relatório de Despesas e Reembolsos',
                                  },
                                );
                                apllyToast(
                                  'success',
                                  `Email enviado para ${expense.manager?.name}!`,
                                );
                              } catch (err: any) {
                                apllyToast(
                                  'error',
                                  err.response?.data?.message ||
                                    'Erro ao enviar email',
                                );
                              }
                            }}
                          >
                            Email Gestor
                          </Button>
                        )}
                      </HStack>
                      )
                    )}
                  </Box>
                )}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Flex justify="space-between" w="100%" align="center">
            <Text fontSize="xs" color="gray.500">
              ID: {expense.id.substring(0, 8)}...
            </Text>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onClose}>
                Fechar
              </Button>
              <Button
                colorScheme="blue"
                leftIcon={<Icon as={RiFileTextLine} />}
                onClick={handleGeneratePDF}
              >
                Gerar PDF
              </Button>
              {/* Botão contábil: aparece quando ambas assinaturas feitas e não recusado */}
              {(expense.signature ||
                expense.signatureStatus === 'signed') &&
                isManagerSigned &&
                expense.status !== 'RECUSADO' &&
                !readOnly && (
                  <Button
                    colorScheme="yellow"
                    leftIcon={<Icon as={RiSendPlaneLine} />}
                    isLoading={sendingAccounting}
                    loadingText="Enviando..."
                    onClick={async () => {
                      setSendingAccounting(true);
                      try {
                        await expenseReportsService.sendToAccounting(
                          expense.id,
                        );
                        apllyToast(
                          'success',
                          'Relatório enviado para lançamento contábil!',
                        );
                      } catch (err: any) {
                        apllyToast(
                          'error',
                          err.response?.data?.message ||
                            'Erro ao enviar para contábil',
                        );
                      } finally {
                        setSendingAccounting(false);
                      }
                    }}
                  >
                    Lançamento Contábil
                  </Button>
                )}
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalComponent>
    </>
  );
}
